import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import { RootState } from 'store/rootReducer';
import get from 'lodash/get';
import set from 'lodash/set';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { Product, ProductsState, UpdateProductListPayload, SearchParams } from './productsTypes';
import { axiosInstance } from '../axiosInstance';

const productsApiPrefix = '/products';

const initialState: ProductsState = {
  apiErrors: {
    loadingError: '',
    savingError: ''
  },
  loaders: {
    isDeletingItems: {}
  },
  isSearch: false,
  list: [],
  pagination: {
    size: 30,
    index: 1,
    indexesCount: 0
  },
  searchList: [],
  searchTerm: '',
  groupComponents: false
};

export const fetchProducts = createAsyncThunk<UpdateProductListPayload, SearchParams, { rejectValue: string }>(
  'products/fetchProducts',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(productsApiPrefix, {
        params
      });

      const {
        data: { products, size, index, indexes_count }
      } = response;
      let isSearch = false;
      if (get(params, 'search') || get(params, 'groupcomponents')) {
        isSearch = true;
      }

      return {
        products,
        isSearch,
        searchTerm: get(params, 'search', ''),
        groupComponents: get(params, 'groupcomponents') ?? false,
        pagination: { size, index, indexesCount: indexes_count }
      };
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('errors.unauthorized'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('products.no product found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    }
  }
);

export const deleteProduct = createAsyncThunk<string, string, { rejectValue: string }>(
  'products/deleteProducts',
  async (productId, { dispatch, rejectWithValue }) => {
    try {
      dispatch(updateListDelete({ id: productId, value: true }));
      await axiosInstance.delete(`${productsApiPrefix}/${productId}`);
      return productId;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('products.you cannot delete this product'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('products.no product found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    } finally {
      dispatch(updateListDelete({ id: productId, value: false }));
    }
  }
);

// TODO: refactor with object
const productsSlice = createSlice({
  initialState,
  name: 'products',
  reducers: {
    updateListDelete: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.deletingErrorItems.${id}`, '');
      }
      set(state, `loaders.isDeletingItems.${id}`, value);
    },
    resetSearchList: state => {
      state.searchList = [];
      state.isSearch = false;
      state.searchTerm = '';
      state.pagination = { ...state.pagination, index: 1 };
    },
    resetProductList: state => {
      state.list = [];
    },
    closeError: state => {
      state.apiErrors = {};
    }
  },
  extraReducers: builder =>
    builder
      .addCase(fetchProducts.fulfilled, (state, { payload }: PayloadAction<UpdateProductListPayload>) => {
        const { products, pagination, isSearch, searchTerm, groupComponents } = payload;
        state.isSearch = isSearch;
        state.pagination = pagination;
        state.searchTerm = searchTerm;
        state.groupComponents = groupComponents;
        state.loaders.isLoading = false;
        if (isSearch) {
          state.searchList = products;
          return;
        }
        state.list = products;
      })
      .addCase(fetchProducts.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchProducts.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(deleteProduct.fulfilled, (state, { payload }) => {
        state.loaders.isDeleting = false;
        const { list } = state;
        const newList = list.filter((obj: Product) => obj.id !== payload);
        state.list = newList;
      })
      .addCase(deleteProduct.pending, state => {
        state.loaders.isDeleting = true;
        state.apiErrors.deletingError = '';
      })
      .addCase(deleteProduct.rejected, (state, { payload }) => {
        state.loaders.isDeleting = false;
        state.apiErrors.deletingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = productsSlice;
export { reducer as products };
export const { closeError, resetSearchList, resetProductList, updateListDelete } = actions;

export const getProductsList = (state: RootState) => state.products.list;
export const getProductsPagination = (state: RootState) => state.products.pagination;
export const getSearchProductsList = (state: RootState) => state.products.searchList;
export const isSearch = (state: RootState) => state.products.isSearch;
export const getLoaders = (state: RootState) => state.products.loaders;
export const getSearchTerm = (state: RootState) => state.products.searchTerm;
export const getGroupComponents = (state: RootState) => state.products.groupComponents;
export const productsApiErrors = (state: RootState) => state.products.apiErrors;
